import type { ColorTheme, Locale } from "@shelflist/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { applyLanguage } from "@/i18n";
import { setBrowserOverride } from "@/i18n/languages";
import { api, authApi, call } from "./api";
import { applyColorTheme } from "./color-theme";
import { type Me, meQuery, queryKeys } from "./queries";

/** Signs out, drops all cached data and returns to the login page. */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    try {
      await authApi.signOut();
    } finally {
      queryClient.setQueryData(queryKeys.me, null);
      applyLanguage(null);
      applyColorTheme(null);
      await navigate({ to: "/login" });
      // Drop cached data of the previous user, except what the login page needs.
      queryClient.removeQueries({
        predicate: (query) =>
          query.queryKey[0] !== queryKeys.me[0] && query.queryKey[0] !== queryKeys.setupStatus[0],
      });
    }
  };
}

/** Call after any flow that creates a session (login, setup, invitation, reset). */
export function useAfterSignIn() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return async (redirectTo = "/") => {
    queryClient.setQueryData(queryKeys.setupStatus, { setupRequired: false });
    // Load the new user before navigating so the signed-in layout sees the session.
    const me = await queryClient.fetchQuery({ ...meQuery, staleTime: 0 });
    applyLanguage(me?.locale);
    applyColorTheme(me?.colorTheme);
    await navigate({ to: redirectTo });
  };
}

/**
 * The signed-in user inside the `/_app` layout (which guarantees a session).
 * Reads the live query cache, so updates (e.g. language, role) re-render immediately.
 */
export function useMe(): Me {
  const { me: atNavigation } = useRouteContext({ from: "/_app" });
  const { data } = useQuery(meQuery);
  // `data` is null for a moment while signing out; keep rendering the last known user.
  return data ?? atNavigation;
}

/**
 * Saves the user's language preference (`null` = browser default) and applies it immediately.
 * Shared by the account page and the user menu.
 */
export function useSetLanguage() {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (locale: Locale | null) => call(api.me.$patch({ json: { locale } })),
    onSuccess: (updated) => {
      const current = queryClient.getQueryData<Me | null>(queryKeys.me);
      if (!current || current.id !== updated.id) return;
      queryClient.setQueryData<Me>(queryKeys.me, (current) =>
        current ? { ...current, locale: updated.locale } : updated,
      );
      // "Browser default" also forgets a language picked on the login page.
      setBrowserOverride(updated.locale);
      applyLanguage(updated.locale);
      toast.success(t("language.saved"));
    },
  });
}

/** Save only the palette, preserving concurrent language updates in the cache. */
export function useSetColorTheme() {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (colorTheme: ColorTheme) => call(api.me.$patch({ json: { colorTheme } })),
    onSuccess: (updated) => {
      const current = queryClient.getQueryData<Me | null>(queryKeys.me);
      // A save may finish after sign-out or after another account signs in.
      if (!current || current.id !== updated.id) return;
      queryClient.setQueryData<Me>(queryKeys.me, (current) =>
        current ? { ...current, colorTheme: updated.colorTheme } : updated,
      );
      applyColorTheme(updated.colorTheme);
      toast.success(t("theme.saved"));
    },
  });
}
